import React, { useEffect, useState } from "react";
import { Switch, Input, Icon, Modal } from "../../";

import TimeEditor from "./TimeEditor";
import BusinessZoneModal from "./BusinessZoneModal";
import {
  numOnly,
  priorityList,
  defaultScheduleStr,
} from "./BusinessZones.utils";

const BusinessZonesStep = ({
  businessId,
  zones = [],
  deliveryTime,
  pickupTime,
  cancelOrderAfterMinutes,
  orderDefaultPriority,
  minimum,
  businessAddress,
  businessLocation,
  onUpdate,
  onToggleZone,
  onCreateZone,
  onUpdateZone,
  onDeleteZone,
  onUpdateZoneBusinesses,
}) => {
  const [tab, setTab] = useState("delivery");

  const [cancelMinutes, setCancelMinutes] = useState(
    String(cancelOrderAfterMinutes ?? ""),
  );
  const [priority, setPriority] = useState(
    orderDefaultPriority != null ? String(orderDefaultPriority) : "0",
  );
  const [pickupMinimum, setPickupMinimum] = useState(String(minimum ?? ""));

  // Individual saving states
  const [savingDeliveryTime, setSavingDeliveryTime] = useState(false);
  const [savingCancelMinutes, setSavingCancelMinutes] = useState(false);
  const [savingPriority, setSavingPriority] = useState(false);
  const [savingPickupTime, setSavingPickupTime] = useState(false);
  const [savingMinimum, setSavingMinimum] = useState(false);

  const [showZoneForm, setShowZoneForm] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [deletingZone, setDeletingZone] = useState(null);

  const [zoneForm, setZoneForm] = useState({
    name: "",
    type: 4,
    minimum: "",
    price: "",
    data: null,
  });

  const resetZoneForm = () => {
    setZoneForm({ name: "", type: 4, minimum: "", price: "", data: null });
    setEditingZone(null);
  };

  const handleOpenCreateZone = () => {
    resetZoneForm();
    setShowZoneForm(true);
  };

  const handleOpenEditZone = (zone) => {
    let parsedData = zone.data || null;
    if (parsedData && typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (error) {
        console.error("Error parsing zone data:", error);
        parsedData = null;
      }
    }

    setZoneForm({
      name: zone.name || "",
      type: zone.type || 4,
      minimum: String(zone.minimum ?? ""),
      price: String(zone.price ?? ""),
      data: parsedData,
    });
    setEditingZone(zone);
    setShowZoneForm(true);
  };

  const handleSubmitZone = async () => {
    const payloadBase = {
      name: zoneForm.name.trim(),
      type: Number(zoneForm.type),
      minimum: String(zoneForm.minimum),
      price: String(zoneForm.price),
      enabled: true,
    };

    const payload = { ...payloadBase };
    if (Number(zoneForm.type) === 4) {
      payload.data = "null";
    } else if (Number(zoneForm.type) === 1) {
      // Circle
      payload.data = JSON.stringify(zoneForm.data || null);
    } else if (Number(zoneForm.type) === 2) {
      // Polygon
      payload.data = JSON.stringify(zoneForm.data || null);
    }

    if (!editingZone) {
      payload.schedule = defaultScheduleStr;
      await onCreateZone?.(payload);
    } else {
      // Build diff update
      const diff = {};
      if (editingZone.name !== payloadBase.name) diff.name = payloadBase.name;
      if (editingZone.type !== payloadBase.type) diff.type = payloadBase.type;
      if (String(editingZone.minimum) !== payloadBase.minimum)
        diff.minimum = payloadBase.minimum;
      if (String(editingZone.price) !== payloadBase.price)
        diff.price = payloadBase.price;
      if (Number(zoneForm.type) === 4) {
        if (editingZone.data !== null) diff.data = null;
      } else if (Number(zoneForm.type) === 1 || Number(zoneForm.type) === 2) {
        // Circle or Polygon
        const dataStr = JSON.stringify(zoneForm.data || null);
        if (
          JSON.stringify(editingZone.data || null) !==
          JSON.stringify(zoneForm.data || null)
        )
          diff.data = dataStr;
      }
      if (Object.keys(diff).length) {
        await onUpdateZone?.(editingZone.id, diff);
      }
    }
    setShowZoneForm(false);
    resetZoneForm();
  };

  useEffect(() => {
    setCancelMinutes(String(cancelOrderAfterMinutes ?? ""));
  }, [cancelOrderAfterMinutes]);

  useEffect(() => {
    setPriority(
      orderDefaultPriority != null ? String(orderDefaultPriority) : "0",
    );
  }, [orderDefaultPriority]);

  useEffect(() => {
    setPickupMinimum(String(minimum ?? ""));
  }, [minimum]);

  return (
    <div className="space-y-6">
      <div className="border-b border-gray-800">
        <nav className="flex space-x-6 overflow-x-auto">
          {[
            { id: "delivery", label: "Delivery" },
            { id: "pickup", label: "Pickup" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`border-b-2 px-1 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                tab === t.id
                  ? "border-blue-500 text-white"
                  : "border-transparent text-gray-400 hover:border-gray-300 hover:text-gray-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {tab === "delivery" && (
        <div className="space-y-6">
          {/* Order time first */}
          <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
            <TimeEditor
              label="Delivery order time (HH:MM)"
              value={deliveryTime}
              onSave={async (val) => {
                try {
                  setSavingDeliveryTime(true);
                  await onUpdate({ delivery_time: val });
                } finally {
                  setSavingDeliveryTime(false);
                }
              }}
              isSaving={savingDeliveryTime}
            />
          </div>

          {/* Zones list */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-base font-medium text-white">
                Delivery Zones
              </h3>
              <button
                onClick={handleOpenCreateZone}
                className="flex items-center gap-1 rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700"
              >
                <Icon name="Plus" className="h-4 w-4" /> Add Zone
              </button>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-700 bg-gray-800/60">
              <div className="grid grid-cols-12 border-b border-gray-700 bg-gradient-to-r from-gray-800 to-gray-700/60 px-4 py-3 text-xs text-gray-300 sm:text-sm">
                <div className="col-span-6">Name</div>
                <div className="col-span-2">Minimum</div>
                <div className="col-span-2">Price</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {!zones || zones.length === 0 ? (
                <div className="p-8 text-center text-gray-300">
                  No zones found
                </div>
              ) : (
                zones.map((z) => (
                  <div
                    key={z.id}
                    className="grid grid-cols-12 items-center border-t border-gray-700 px-4 py-3 text-white hover:bg-gray-700/40"
                  >
                    <div className="col-span-6">
                      <div className="mr-6 flex min-w-0 items-center gap-2">
                        <span className="truncate font-medium" title={z.name}>
                          {z.name}
                        </span>
                        {businessId === z.business_id &&
                          z?.businesses?.length > 1 && (
                            <span
                              className="inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-blue-500/30 bg-blue-500/20 px-2 py-0.5 text-xs font-medium text-blue-300"
                              title={`Shared with ${z.businesses.length - 1} other business${z.businesses.length - 1 > 1 ? "es" : ""}`}
                            >
                              <Icon name="Users" className="h-3 w-3" />
                              <span>Shared</span>
                            </span>
                          )}
                      </div>
                    </div>
                    <div className="col-span-2">{z.minimum ?? "—"}</div>
                    <div className="col-span-2">{z.price ?? "—"}</div>
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <Switch
                        checked={!!z.enabled}
                        onCheckedChange={(value) => onToggleZone(z.id, value)}
                      />
                      <button
                        onClick={() => handleOpenEditZone(z)}
                        className="rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs hover:bg-gray-700"
                        title="Edit zone"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => setDeletingZone(z)}
                        className={`rounded border border-red-500/60 px-2 py-1 text-xs text-red-300 hover:bg-red-900/30 ${businessId === z.business_id && z?.businesses?.length > 1 ? "cursor-not-allowed opacity-50" : ""}`}
                        title="Delete zone"
                        disabled={
                          businessId === z.business_id &&
                          z?.businesses?.length > 1
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Bottom side-by-side controls */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
              <label className="mb-2 block text-sm font-medium text-gray-300">
                Cancel order after minutes
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={cancelMinutes}
                  onChange={(e) => setCancelMinutes(numOnly(e.target.value))}
                  placeholder="e.g. 1441"
                />
                <button
                  onClick={async () => {
                    try {
                      setSavingCancelMinutes(true);
                      await onUpdate({
                        cancel_order_after_minutes: cancelMinutes,
                      });
                    } finally {
                      setSavingCancelMinutes(false);
                    }
                  }}
                  disabled={
                    savingCancelMinutes ||
                    String(cancelOrderAfterMinutes ?? "") ===
                      String(cancelMinutes)
                  }
                  className="flex items-center justify-center rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingCancelMinutes ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
              <label className="mb-2 block text-sm font-medium text-gray-300">
                Order default priority
              </label>
              <div className="flex items-center gap-2">
                <select
                  className="custom-select h-10 w-full rounded-md border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-white focus:border-transparent focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {priorityList.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.content}
                    </option>
                  ))}
                </select>
                <button
                  onClick={async () => {
                    try {
                      setSavingPriority(true);
                      await onUpdate({ order_default_priority: priority });
                    } finally {
                      setSavingPriority(false);
                    }
                  }}
                  disabled={
                    savingPriority ||
                    String(orderDefaultPriority ?? "0") === String(priority)
                  }
                  className="flex items-center justify-center rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingPriority ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === "pickup" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
              <TimeEditor
                label="Pickup order time (HH:MM)"
                value={pickupTime}
                onSave={async (val) => {
                  try {
                    setSavingPickupTime(true);
                    await onUpdate({ pickup_time: val });
                  } finally {
                    setSavingPickupTime(false);
                  }
                }}
                isSaving={savingPickupTime}
              />
            </div>
            <div className="rounded-xl border border-gray-700 bg-gray-800/60 p-4">
              <label className="mb-2 block text-sm font-medium text-gray-300">
                Minimum purchase
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={pickupMinimum}
                  onChange={(e) => setPickupMinimum(numOnly(e.target.value))}
                  placeholder="e.g. 3"
                />
                <button
                  onClick={async () => {
                    try {
                      setSavingMinimum(true);
                      await onUpdate({ minimum: pickupMinimum });
                    } finally {
                      setSavingMinimum(false);
                    }
                  }}
                  disabled={
                    savingMinimum ||
                    String(minimum ?? "") === String(pickupMinimum)
                  }
                  className="flex items-center justify-center rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingMinimum ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Create/Edit Zone Modal */}
      {showZoneForm ? (
        <BusinessZoneModal
          isOpen={true}
          onClose={() => {
            setShowZoneForm(false);
            resetZoneForm();
          }}
          editingZone={editingZone}
          zoneForm={zoneForm}
          setZoneForm={setZoneForm}
          businessAddress={businessAddress}
          businessLocation={businessLocation}
          businessId={businessId}
          onSubmitZone={handleSubmitZone}
          onUpdateZoneBusinesses={onUpdateZoneBusinesses}
        />
      ) : null}

      {/* Delete Zone Confirm */}
      {deletingZone && (
        <Modal
          isOpen={true}
          onClose={() => setDeletingZone(null)}
          title="Delete Zone"
          subtitle="Are you sure you want to delete this zone?"
          maxWidth="max-w-md"
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
              <div className="flex items-center space-x-3">
                <div>
                  <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                    {deletingZone.name}
                  </h4>
                  <p className="mt-1 text-xs text-red-600 dark:text-red-300">
                    This action cannot be undone.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex space-x-3 pt-4">
              <button
                onClick={async () => {
                  await onDeleteZone?.(deletingZone.id);
                  setDeletingZone(null);
                }}
                className="flex-1 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
              >
                Delete Zone
              </button>
              <button
                onClick={() => setDeletingZone(null)}
                className="text-card-foreground bg-card border-border hover:bg-accent flex-1 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default BusinessZonesStep;
